import { useCallback } from 'react'
import { useAppDispatch } from '@/state'
import { debounce } from 'lodash'

import { FormStatusType } from '@/enums/form-status.enum'
import {
  DeleteBonusPayload,
  GetBonusesPayload,
  GetOneBonusPayload,
  BonusesFilters,
  XlsxBonusProgramType,
  BonusLibraryColumnProps,
} from '@/state/bonus-engine/models'
import {
  resetCurrentBonusAction,
  setBonusFormModeAction,
  setBonusesFiltersAction,
} from '@/state/bonus-engine/slices/index.slices'

import { deleteBonusEngineThunk } from '@/state/bonus-engine/thunks/delete.thunk'
import { getAllBonusesThunk } from '@/state/bonus-engine/thunks/get-all.thunk'
import { getOneBonusThunk } from '@/state/bonus-engine/thunks/get-bonus.thunk'

import { TableHeadCellPropsType } from '@/state/ui/global/models'
import { exportXlsxFile, removeEmptyProp } from '@/utils'

export const useBonusActions = () => {
  const dispatch = useAppDispatch()

  const getAllBonuses = async (
    filters: GetBonusesPayload,
    headCells?: TableHeadCellPropsType<BonusLibraryColumnProps>[],
    fileName?: string,
    getFormattedRow?: (row: BonusLibraryColumnProps) => XlsxBonusProgramType,
  ) => {
    const newFilters = removeEmptyProp(filters)

    const resultAction = await dispatch(
      getAllBonusesThunk({
        ...newFilters,
        xlsx: !!headCells,
      }),
    )

    if (
      getFormattedRow &&
      headCells &&
      getAllBonusesThunk.fulfilled.match(resultAction)
    ) {
      exportXlsxFile(
        headCells,
        resultAction.payload.docs.map((item) => getFormattedRow(item)),
        fileName,
      )
    }
  }

  const getAllBonusesInit = useCallback(
    (filters: Omit<GetBonusesPayload, 'page' | 'limit'>) => {
      dispatch(
        getAllBonusesThunk({
          ...filters,
          page: 1,
          limit: 9999,
        }),
      )
    },
    [dispatch], // eslint-disable-line
  )

  const getBonusByIdOrCode = useCallback(
    (payload: GetOneBonusPayload) => {
      dispatch(
        getOneBonusThunk({
          ...payload,
        }),
      )
    },
    [dispatch], // eslint-disable-line
  )

  const resetCurrentBonus = () => {
    dispatch(resetCurrentBonusAction())
  }

  const setBonusFormTypeAction = useCallback(
    (formMode: FormStatusType) => dispatch(setBonusFormModeAction(formMode)),
    [dispatch],
  )

  const setBonusFilters = useCallback(
    (filters: BonusesFilters) => dispatch(setBonusesFiltersAction(filters)),
    [dispatch],
  )

  const deleteBonus = debounce(
    (payload: DeleteBonusPayload) =>
      dispatch(deleteBonusEngineThunk({ ...payload })),
    500,
  )

  return {
    resetCurrentBonusAction: resetCurrentBonus,
    getAllBonuses,
    getAllBonusesInit,
    getBonusByIdOrCode,
    setBonusFormTypeAction,
    setBonusFilters,
    deleteBonus,
  }
}
